/* Revenue Dashboard — accounts/revenue + recent-payments backed.
 *
 * Range buttons: Daily / Weekly / Monthly / Quarterly / Half-Yearly / Yearly /
 * Custom (with from-to pickers). Cards: total, payment count, unique patients,
 * invoice count. Charts: line of daily timeseries, breakdown tables for method
 * / referring doctor / test / product-type.
 *
 * ── 2026-08-29 · Pass 2 ──
 * Product-type breakdown card, branch filter, drill-downs on every KPI card,
 * breakdown row, chart data point and recent-payments row. Recent-payments
 * "ADVANCE" label renamed to "ADVANCE APPLIED" so the whole page speaks the
 * same language as the by_method breakdown.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Calendar, IndianRupee, Users, FileText, RefreshCw, Stethoscope,
  Building2, Wrench, Ear, ShoppingBag, MoreHorizontal, ChevronDown, ChevronUp,
  Download,
} from 'lucide-react';
import {
  ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Area, AreaChart,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RANGES = [
  { key: 'daily',       label: 'Today' },
  { key: 'weekly',      label: 'Last 7 days' },
  { key: 'monthly',     label: 'Last 30 days' },
  { key: 'quarterly',   label: 'Last 90 days' },
  { key: 'half_yearly', label: 'Last 6 months' },
  { key: 'yearly',      label: 'Last year' },
  { key: 'custom',      label: 'Custom range' },
];

const fmtINR = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// Sep 2026 · Revenue Streams — 5-way breakdown replacing the old 4
// product-type breakdown cards. Each stream card knows its icon, tone
// and drill-down route so the layout stays declarative.
const STREAM_META = {
  diagnostics: {
    label: 'Diagnostics (Hearing Tests)',
    icon: Stethoscope,
    tone: 'sky',
    bar: 'from-sky-400 to-sky-600',
    drill: '/billing?q=PTA',
  },
  services_repairs: {
    label: 'Services & Repairs',
    icon: Wrench,
    tone: 'amber',
    bar: 'from-amber-400 to-amber-600',
    drill: '/repair/jobs',
  },
  hearing_aid_sales: {
    label: 'Hearing Aid Sales',
    icon: Ear,
    tone: 'indigo',
    bar: 'from-indigo-400 to-indigo-600',
    drill: '/ha/fittings',
  },
  accessories_sold: {
    label: 'Accessories Sold',
    icon: ShoppingBag,
    tone: 'teal',
    bar: 'from-teal-400 to-teal-600',
    drill: '/billing?product_type=Accessory',
  },
  others: {
    label: 'Others',
    icon: MoreHorizontal,
    tone: 'slate',
    bar: 'from-slate-300 to-slate-500',
    drill: '/billing',
  },
};

const STREAM_TONE_CARD = {
  sky:    'from-sky-50 to-sky-100 border-sky-200',
  amber:  'from-amber-50 to-amber-100 border-amber-200',
  indigo: 'from-indigo-50 to-indigo-100 border-indigo-200',
  teal:   'from-teal-50 to-teal-100 border-teal-200',
  slate:  'from-slate-50 to-slate-100 border-slate-200',
};
const STREAM_TONE_TEXT = {
  sky: 'text-sky-900', amber: 'text-amber-900', indigo: 'text-indigo-900',
  teal: 'text-teal-900', slate: 'text-slate-800',
};

// Human labels for the by_method aggregation keys.
const METHOD_LABELS = {
  cash:               'Cash',
  card:               'Card',
  upi:                'UPI',
  bank_transfer:      'Bank transfer',
  cheque:             'Cheque',
  advance:            'Advance applied',       // legacy rows only
  advance_applied:    'Advance applied',
  advance_collected:  'Advance collected',
  other:              'Other',
};

// Recent-payments METHOD column label. Aligns with the by_method
// breakdown so both surfaces speak the same language. Everything else
// falls through to upper-case of the raw method string.
const RECENT_METHOD_LABEL = {
  advance:            'ADVANCE APPLIED',
  advance_applied:    'ADVANCE APPLIED',
  advance_collected:  'ADVANCE COLLECTED',
};

export default function AccountsRevenuePage() {
  const navigate = useNavigate();

  const [range, setRange] = useState('monthly');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [branchId, setBranchId] = useState('');
  const [branches, setBranches] = useState([]);
  const [data, setData] = useState(null);
  const [recent, setRecent] = useState([]);
  const [streams, setStreams] = useState(null);        // Sep 2026 · 5-way
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch branches once on mount so the multi-branch dropdown can render.
  // Single-branch tenants still see the dropdown but it degenerates to a
  // single option — kept for consistency across tenants.
  useEffect(() => {
    axios.get(`${API}/branches`).then((r) => setBranches(r.data || []))
      .catch(() => { /* branch dropdown is a soft dependency — page still loads */ });
  }, []);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const params = { range };
      if (branchId) params.branch_id = branchId;
      if (range === 'custom') {
        if (!from || !to) { setError('Pick a from and to date'); setLoading(false); return; }
        params.from = from; params.to = to;
      }
      const recentParams = { limit: 30 };
      if (branchId) recentParams.branch_id = branchId;
      const [r1, r2, r3] = await Promise.all([
        axios.get(`${API}/accounts/revenue`,          { params }),
        axios.get(`${API}/accounts/recent-payments`,  { params: recentParams }),
        axios.get(`${API}/accounts/revenue-streams`,  { params }).catch(() => ({ data: null })),
      ]);
      setData(r1.data);
      setRecent(r2.data || []);
      setStreams(r3.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  // Re-load on range or branch flip. Custom range waits for Apply.
  useEffect(() => {
    if (range !== 'custom' || (from && to)) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, branchId]);

  const chartData = useMemo(() => (data?.timeseries || []).map(d => ({
    label: d.date.slice(5),
    date: d.date,
    amount: d.amount,
    count: d.count,
  })), [data]);

  // Drill-down helpers. Each surface knows the exact filtered detail
  // page to open — no dead links anywhere on the dashboard.
  const branchQS = branchId ? `&branch_id=${encodeURIComponent(branchId)}` : '';
  const drill = {
    total:            () => navigate(`/billing?status=paid${branchQS}`),
    payments:         () => navigate(`/billing/payments${branchId ? `?branch_id=${encodeURIComponent(branchId)}` : ''}`),
    invoices:         () => navigate(`/billing${branchQS ? `?branch_id=${encodeURIComponent(branchId)}` : ''}`),
    patients:         () => navigate('/patients'),
    day:              (dateStr) => navigate(`/billing?from_date=${dateStr}&to_date=${dateStr}${branchQS}`),
    invoice:          (invId) => invId
      ? navigate(`/billing/invoice/${encodeURIComponent(invId)}`)
      : navigate(`/billing${branchQS ? `?branch_id=${encodeURIComponent(branchId)}` : ''}`),
  };

  const activeBranchLabel = branchId
    ? (branches.find((b) => b.branch_id === branchId)?.name || branchId)
    : 'All branches';

  // The transactions endpoint needs the same params as /revenue, plus
  // the stream key. Passed to <StreamCard> so each card fetches on
  // expand.
  const streamParams = useMemo(() => {
    const p = { range };
    if (branchId) p.branch_id = branchId;
    if (range === 'custom' && from && to) { p.from = from; p.to = to; }
    return p;
  }, [range, branchId, from, to]);

  return (
    <div className="p-6 space-y-6 bg-slate-50 min-h-screen" data-testid="accounts-revenue-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <IndianRupee size={24} className="text-emerald-600" /> Revenue Dashboard
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Accounts overview · payments aggregated by date, doctor, test, product type &amp; method
            {branchId && (
              <> · <span className="font-semibold text-slate-700">{activeBranchLabel}</span></>
            )}
          </p>
        </div>
        <button
          data-testid="accounts-refresh"
          onClick={load}
          className="px-3 py-1.5 text-xs font-semibold bg-white border border-slate-200 rounded-lg shadow-sm hover:bg-slate-50 inline-flex items-center gap-1.5"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </header>

      {/* Range + branch selectors */}
      <div className="bg-white rounded-2xl shadow-sm p-4 border border-slate-200">
        <div className="flex flex-wrap gap-3 items-start">
          <div className="flex flex-wrap gap-2 flex-1 min-w-0">
            {RANGES.map(r => (
              <button
                key={r.key}
                data-testid={`range-${r.key}`}
                onClick={() => setRange(r.key)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${
                  range === r.key
                    ? 'bg-slate-900 text-white shadow'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <label htmlFor="branch-filter" className="text-[10px] uppercase font-semibold text-slate-500 flex items-center gap-1">
              <Building2 size={11} /> Branch
            </label>
            <select
              id="branch-filter"
              data-testid="branch-filter"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              className="px-2 py-1.5 text-xs font-semibold border border-slate-300 rounded bg-white min-w-[180px]"
            >
              <option value="" data-testid="branch-option-all">All branches</option>
              {branches.map(b => (
                <option key={b.branch_id} value={b.branch_id} data-testid={`branch-option-${b.branch_id}`}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {range === 'custom' && (
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <div>
              <label className="text-[10px] uppercase font-semibold text-slate-500 block mb-0.5">From</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="px-2 py-1.5 text-xs border border-slate-300 rounded" data-testid="range-from" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-semibold text-slate-500 block mb-0.5">To</label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="px-2 py-1.5 text-xs border border-slate-300 rounded" data-testid="range-to" />
            </div>
            <button onClick={load} className="px-3 py-1.5 text-xs font-semibold bg-slate-900 text-white rounded" data-testid="range-apply">Apply</button>
          </div>
        )}

        {data && (
          <div className="text-[11px] text-slate-500 mt-3 flex items-center gap-1.5">
            <Calendar size={11} /> {data.from} → {data.to}
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-800" data-testid="accounts-error">{error}</div>
      )}

      {/* KPI cards — every one drills to a filtered detail page */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Total revenue"           value={fmtINR(data?.total)}            tone="emerald" testid="kpi-total"    loading={loading} onClick={drill.total}    icon={IndianRupee} />
        <KPI label="Payments"                 value={data?.payment_count ?? '—'}     tone="indigo"  testid="kpi-payments" loading={loading} onClick={drill.payments} icon={FileText} />
        <KPI label="Unique paying patients"   value={data?.unique_patients ?? '—'}   tone="fuchsia" testid="kpi-patients" loading={loading} onClick={drill.patients} icon={Users} />
        <KPI label="Invoices"                 value={data?.invoice_count ?? '—'}     tone="amber"   testid="kpi-invoices" loading={loading} onClick={drill.invoices} icon={FileText} />
      </div>

      {/* Timeseries chart with click-through per day */}
      <div className="bg-white rounded-2xl shadow-sm p-5 border border-slate-200" data-testid="revenue-chart">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="text-sm font-bold text-slate-900">Daily revenue</h2>
          <span className="text-[10px] uppercase tracking-wider text-slate-500">
            {(chartData || []).length} days · click a point to drill to that day
          </span>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={chartData} style={{ cursor: 'pointer' }}>
            <defs>
              <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.6} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v, n) => n === 'amount' ? fmtINR(v) : v} />
            <Area
              type="monotone"
              dataKey="amount"
              stroke="#10b981"
              strokeWidth={2}
              fill="url(#colorRev)"
              activeDot={{
                r: 5,
                style: { cursor: 'pointer' },
                onClick: (_evt, payload) => {
                  const pt = payload?.payload;
                  if (pt?.date) drill.day(pt.date);
                },
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Sep 2026 · 5-Stream Revenue Breakdown (replaces the 4 old
          product-type / doctor / test / method cards). Sum of streams
          ties back exactly to the Total Revenue KPI above. */}
      <RevenueStreamsSection
        streams={streams}
        loading={loading}
        params={streamParams}
        onOpenInvoice={drill.invoice}
      />

      {/* Recent payments — every row clicks through to its invoice */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200" data-testid="recent-payments">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <FileText size={14} className="text-slate-500" />
          <h2 className="text-sm font-bold text-slate-900">Recent payments</h2>
          <span className="text-[10px] uppercase tracking-wider text-slate-500 ml-auto">{recent.length} most recent · click a row to open the invoice</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Date</th>
                <th className="px-4 py-2 text-left">Method</th>
                <th className="px-4 py-2 text-left">Reference</th>
                <th className="px-4 py-2 text-left">Tests</th>
                <th className="px-4 py-2 text-left">Ref. Dr</th>
                <th className="px-4 py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400 italic">No payments in window</td></tr>}
              {recent.map(p => {
                const methodLabel = RECENT_METHOD_LABEL[p.method] || (p.method || '').toUpperCase();
                return (
                  <tr
                    key={p.payment_id}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                    data-testid={`payment-row-${p.payment_id}`}
                    onClick={() => drill.invoice(p.invoice_id)}
                    title={p.invoice_id ? `Open invoice ${p.invoice_id}` : 'Open billing'}
                  >
                    <td className="px-4 py-2 font-mono text-[11px]">{(p.paid_at || '').slice(0, 10)}</td>
                    <td className="px-4 py-2 font-semibold text-slate-700">{methodLabel}</td>
                    <td className="px-4 py-2 font-mono text-[11px]">{p.reference || '—'}</td>
                    <td className="px-4 py-2">{(p.tests || []).join(' + ') || '—'}</td>
                    <td className="px-4 py-2">{p.referring_doctor_name || <span className="text-slate-400">walk-in</span>}</td>
                    <td className="px-4 py-2 text-right font-bold tabular-nums">{fmtINR(p.amount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {/* eslint-disable-next-line no-unused-vars */}
      <span className="hidden">{METHOD_LABELS.cash}</span>
    </div>
  );
}

const TONES = {
  emerald: 'from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-900',
  indigo:  'from-indigo-50  to-indigo-100  border-indigo-200  text-indigo-900',
  fuchsia: 'from-fuchsia-50 to-fuchsia-100 border-fuchsia-200 text-fuchsia-900',
  amber:   'from-amber-50   to-amber-100   border-amber-200   text-amber-900',
};

function KPI({ label, value, tone = 'emerald', testid, loading, onClick, icon: Icon }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`text-left rounded-xl p-4 bg-gradient-to-br border min-w-0 overflow-hidden transition-all hover:shadow-md hover:-translate-y-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-slate-400 ${TONES[tone]}`}
      title={`Open ${label.toLowerCase()} detail`}
    >
      <div className="flex items-center gap-1.5">
        {Icon && <Icon size={12} className="opacity-70 shrink-0" />}
        <div className="text-[10px] font-semibold uppercase tracking-wider opacity-70 truncate">{label}</div>
      </div>
      <div className="text-2xl font-bold mt-1 truncate" title={String(value)}>{loading ? '…' : value}</div>
    </button>
  );
}


/* ═════════════════════════════════════════════════════════════════════
   Sep 2026 · Revenue-Streams section (5-way breakdown).
   Sums of stream cards tie back exactly to the Total Revenue KPI card.
   Each card is independently expandable — clicking the chevron fetches
   /accounts/revenue-streams/{stream}/transactions for the same date /
   branch window and renders a scrollable transactions table inline.
   ═════════════════════════════════════════════════════════════════════ */
function RevenueStreamsSection({ streams, loading, params, onOpenInvoice }) {
  const rows = streams?.streams || [];
  const grand = Number(streams?.total || 0) || 1;
  return (
    <div data-testid="revenue-streams-section">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-bold text-slate-900">Revenue by Stream</h2>
        <span className="text-[10px] uppercase tracking-wider text-slate-500">
          {loading ? 'Loading…' : `${rows.length} streams · sum = ${fmtINR(streams?.total || 0)}`}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
        {rows.map((r) => (
          <StreamCard
            key={r.stream}
            row={r}
            grand={grand}
            params={params}
            onOpenInvoice={onOpenInvoice}
          />
        ))}
        {rows.length === 0 && (
          <div className="col-span-full text-center text-xs italic text-slate-400 py-6 bg-white border border-slate-200 rounded-2xl">
            {loading ? 'Loading revenue streams…' : 'No revenue in the selected window.'}
          </div>
        )}
      </div>
    </div>
  );
}


function StreamCard({ row, grand, params, onOpenInvoice }) {
  const meta = STREAM_META[row.stream] || STREAM_META.others;
  const Icon = meta.icon;
  const pct = grand > 0 ? (row.revenue / grand) * 100 : 0;
  const [expanded, setExpanded] = useState(false);
  const [txns, setTxns] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Sep 2026 · CSV download state — kept per-card so an accountant can
  // fire 5 downloads in a row without blocking on the first one's spinner.
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvErr, setCsvErr] = useState(null);

  // Reset expansion + reload transactions when the parent params
  // change (date range / branch swap). Keeps expand-collapse state
  // per card but always re-fetches with fresh filters.
  useEffect(() => {
    if (!expanded) return;
    let cancelled = false;
    (async () => {
      setBusy(true); setErr(null);
      try {
        const r = await axios.get(
          `${API}/accounts/revenue-streams/${row.stream}/transactions`,
          { params: { ...params, limit: 200 } },
        );
        if (!cancelled) setTxns(r.data?.transactions || []);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [expanded, params, row.stream]);

  // Download the current window as CSV. Uses axios so the Bearer token
  // rides on the request; blob-URL swap is the cleanest way to force
  // a download without opening a new tab.
  const downloadCsv = async (e) => {
    // Stop propagation so a card-level click handler (if added later)
    // doesn't also fire.
    if (e) e.stopPropagation();
    setCsvBusy(true); setCsvErr(null);
    try {
      const r = await axios.get(
        `${API}/accounts/revenue-streams/${row.stream}/transactions`,
        {
          params: { ...params, limit: 1000, format: 'csv' },
          responseType: 'blob',
        },
      );
      // Pull the server-provided filename out of Content-Disposition
      // when present, otherwise synthesize a sensible fallback.
      const cd = r.headers?.['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : `revenue-${row.stream}.csv`;
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (ex) {
      setCsvErr(ex?.response?.data?.detail || ex.message || 'Download failed');
    } finally {
      setCsvBusy(false);
    }
  };

  const secondaryCount = row.stream === 'services_repairs'
    ? { label: 'Jobs', value: row.job_count ?? 0 }
    : { label: 'Patients', value: row.patient_count ?? 0 };
  const hasData = row.invoice_count > 0 || row.revenue > 0;

  return (
    <div
      className={`rounded-2xl border shadow-sm bg-gradient-to-br ${STREAM_TONE_CARD[meta.tone]} ${expanded ? 'xl:col-span-3' : ''}`}
      data-testid={`stream-card-${row.stream}`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <Icon size={16} className={`shrink-0 ${STREAM_TONE_TEXT[meta.tone]}`} />
            <h3 className={`text-sm font-bold truncate ${STREAM_TONE_TEXT[meta.tone]}`} title={row.label}>
              {row.label}
            </h3>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={downloadCsv}
              disabled={csvBusy || !hasData}
              title={hasData ? 'Download this stream as CSV for the selected window' : 'No transactions to export'}
              data-testid={`stream-card-${row.stream}-csv`}
              className="text-[10px] font-semibold text-slate-600 hover:text-slate-900 disabled:text-slate-300 disabled:cursor-not-allowed inline-flex items-center gap-1"
            >
              {csvBusy ? '…' : <><Download size={12} /> CSV</>}
            </button>
            <button
              type="button"
              onClick={() => setExpanded((x) => !x)}
              data-testid={`stream-card-${row.stream}-expand`}
              className="text-[10px] font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
            >
              {expanded ? <>Collapse <ChevronUp size={12} /></> : <>Expand <ChevronDown size={12} /></>}
            </button>
          </div>
        </div>

        <div className={`text-2xl font-bold tabular-nums ${STREAM_TONE_TEXT[meta.tone]}`}
             data-testid={`stream-card-${row.stream}-revenue`}
             title={fmtINR(row.revenue)}>
          {fmtINR(row.revenue)}
        </div>
        <div className="mt-1 h-1.5 bg-white/60 rounded overflow-hidden">
          <div
            className={`h-1.5 bg-gradient-to-r ${meta.bar}`}
            style={{ width: `${Math.max(2, pct)}%` }}
          />
        </div>
        <div className="mt-2 flex items-center justify-between text-[11px]">
          <span className="text-slate-700">
            <b data-testid={`stream-card-${row.stream}-invoices`}>{row.invoice_count}</b> invoice{row.invoice_count === 1 ? '' : 's'}
          </span>
          <span className="text-slate-700">
            <b data-testid={`stream-card-${row.stream}-secondary`}>{secondaryCount.value}</b> {secondaryCount.label.toLowerCase()}
          </span>
          <span className="text-[10px] text-slate-500 font-mono">{pct.toFixed(1)}%</span>
        </div>
        {csvErr && (
          <div className="mt-2 text-[10px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1"
               data-testid={`stream-card-${row.stream}-csv-error`}>
            {csvErr}
          </div>
        )}
      </div>

      {expanded && (
        <div className="border-t border-white/70 bg-white/70 rounded-b-2xl"
             data-testid={`stream-card-${row.stream}-detail`}>
          <div className="max-h-72 overflow-y-auto">
            {busy && (
              <div className="p-4 text-center text-xs italic text-slate-500">Loading transactions…</div>
            )}
            {!busy && err && (
              <div className="p-4 text-center text-xs text-rose-700 bg-rose-50">{err}</div>
            )}
            {!busy && !err && (txns?.length ?? 0) === 0 && (
              <div className="p-4 text-center text-xs italic text-slate-500">
                No transactions in this stream for the selected window.
              </div>
            )}
            {!busy && !err && (txns?.length ?? 0) > 0 && (
              <table className="w-full text-[11px]">
                <thead className="bg-slate-50 text-[9px] uppercase tracking-wider text-slate-500 sticky top-0">
                  <tr>
                    <th className="px-3 py-1.5 text-left">Date</th>
                    <th className="px-3 py-1.5 text-left">Invoice / Ref</th>
                    <th className="px-3 py-1.5 text-left">Patient</th>
                    <th className="px-3 py-1.5 text-left">Description</th>
                    <th className="px-3 py-1.5 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {txns.map((t) => (
                    <tr
                      key={t.payment_id}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => onOpenInvoice(t.invoice_id)}
                      data-testid={`stream-txn-${t.payment_id}`}
                      title={t.invoice_id ? `Open invoice ${t.invoice_no || ''}` : ''}
                    >
                      <td className="px-3 py-1.5 font-mono text-[10px]">{(t.paid_at || '').slice(0, 10)}</td>
                      <td className="px-3 py-1.5 font-mono text-[10px]">
                        {t.invoice_no || '—'}
                        {t.ticket_no && (
                          <span className="ml-1 text-amber-700 bg-amber-50 px-1 rounded font-semibold">
                            {t.ticket_no}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 truncate max-w-[140px]" title={t.patient_name || '—'}>
                        {t.patient_name || <span className="text-slate-400 italic">—</span>}
                      </td>
                      <td className="px-3 py-1.5 truncate max-w-[220px]" title={t.description}>
                        {t.description}
                      </td>
                      <td className="px-3 py-1.5 text-right font-bold tabular-nums">
                        {fmtINR(t.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
